import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";

import useNotification from "../components/notifications/useNotifications";
import useTriageSession from "../data/useTriageSession";

export default function useTriageTimers(triageId?: string) {
  const { addNotification } = useNotification();
  const [nowMs, setNowMs] = useState<number>(Date.now());
  const { sessions: triageSessions, startSession, pauseSession, finishSession } = useTriageSession(triageId);

  useEffect(() => {
    const interval = setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const parseApiUtcDate = (value: string) => DateTime.fromISO(value, { zone: "utc" });

  const sessionRows = triageSessions.data?.data ?? [];

  const totalElapsedSeconds = useMemo(() => {
    return sessionRows.reduce((total, row) => {
      if (!row.dismantlingStart) return total;

      const start = parseApiUtcDate(row.dismantlingStart);
      const end = row.dismantlingEnd
        ? parseApiUtcDate(row.dismantlingEnd)
        : DateTime.utc();
      const diff = Math.floor(end.diff(start, "seconds").seconds);

      return total + (diff > 0 ? diff : 0);
    }, 0);
  }, [sessionRows, nowMs]);

  const firstStartedSession = useMemo(() => {
    return [...sessionRows]
      .filter((x) => !!x.dismantlingStart)
      .sort((a, b) => parseApiUtcDate(a.dismantlingStart!).toMillis() - parseApiUtcDate(b.dismantlingStart!).toMillis())[0];
  }, [sessionRows]);

  const lastSessionActivity = useMemo(() => {
    return [...sessionRows].sort((a, b) => {
      const aActivity = a.dismantlingEnd ?? a.dismantlingStart ?? a.created;
      const bActivity = b.dismantlingEnd ?? b.dismantlingStart ?? b.created;
      return parseApiUtcDate(bActivity).toMillis() - parseApiUtcDate(aActivity).toMillis();
    })[0];
  }, [sessionRows]);

  const hasActiveSession = useMemo(() => sessionRows.some((x) => !x.dismantlingEnd), [sessionRows]);
  const isSessionActionLoading = startSession.isLoading || pauseSession.isLoading || finishSession.isLoading;
  const isSessionsLoading = triageSessions.isLoading;

  const handleStart = () => {
    if (!triageId) return;

    startSession.mutate(
      { triageId },
      {
        onSuccess: () => {
          addNotification({
            variant: "success",
            primaryText: "Triage timer started",
          });
        },
        onError: (error: any) => {
          addNotification({
            variant: "danger",
            primaryText: error?.message ?? "Unable to start triage timer",
          });
        }
      }
    );
  };

  const handlePause = (userId: string, pauseReason = "Paused") => {
    if (!triageId) return;

    pauseSession.mutate(
      { triageId, userId, pauseReason },
      {
        onSuccess: () => {
          addNotification({
            variant: "success",
            primaryText: "Triage timer paused",
          });
        },
        onError: (error: any) => {
          addNotification({
            variant: "danger",
            primaryText: error?.message ?? "Unable to pause triage timer",
          });
        }
      }
    );
  };

  const handleFinish = (userId: string) => {
    if (!triageId) return;

    finishSession.mutate(
      { triageId, userId },
      {
        onSuccess: () => {
          addNotification({
            variant: "success",
            primaryText: "Triage timer finished",
          });
        },
        onError: (error: any) => {
          addNotification({
            variant: "danger",
            primaryText: error?.message ?? "Unable to finish triage timer",
          });
        }
      }
    );
  };

  return {
    sessionRows,
    totalElapsedSeconds,
    firstStartedAt: firstStartedSession?.dismantlingStart ?? null,
    lastActivityAt: lastSessionActivity
      ? (lastSessionActivity.dismantlingEnd ?? lastSessionActivity.dismantlingStart ?? lastSessionActivity.created)
      : null,
    hasActiveSession,
    isSessionsLoading,
    isSessionActionLoading,
    handleStart,
    handlePause,
    handleFinish,
  };
}
